import React from 'react';

import BlockContent from '@sanity/block-content-to-react';
import { FaRegQuestionCircle } from 'react-icons/fa';
import { ButtonLinkType } from '../../graphql-fragments/ButtonLink';
import { SanityImageType } from '../../graphql-fragments/SanityImage';
import serializers from '../../serializers';
import { RawPortableText } from '../../types/types';
import { getUrlFromVersatileLink } from '../../utils/sanity';
import { clsx } from '../../utils/utils';
import Image from './Image';
import * as styles from './ListItem.module.scss';
import TextLink from './TextLink';
export type ListItemProps = {
  centerAligned?: boolean;
  horizontalLayout?: boolean;
  lessGapBetweenElements?: boolean;
  title: string;
  additionalExplanation?: string;
  text: string | RawPortableText;
  link?: ButtonLinkType;
  className?: string;
} & (
  | {
      listType: 'withImage';
      imageSize: 'small' | 'large';
      image?: SanityImageType;
      index?: never;
    }
  | {
      listType: 'withNumber';
      imageSize?: never;
      image?: never;
      index: number;
    }
  | {
      listType: 'withBullet';
      imageSize?: never;
      image?: never;
      index?: never;
    }
  | {
      listType: 'withoutMarkers';
      imageSize?: never;
      image?: never;
      index?: never;
    }
);

const ListItem = ({
  centerAligned,
  horizontalLayout,
  lessGapBetweenElements,
  imageSize,
  listType,
  image,
  title,
  additionalExplanation,
  text,
  link,
  index,
  className,
}: ListItemProps): React.ReactElement => {
  return (
    <li className={clsx(styles.itemContainer)}>
      <div
        className={clsx(
          className,
          styles.wrapper,
          centerAligned && styles.centerAligned,
          horizontalLayout && styles.horizontalLayout,
          lessGapBetweenElements && styles.lessGapBetweenElements,
          imageSize === 'large' && styles.withLargerImage,
          image && styles.withImage,
        )}
      >
        {listType === 'withImage' && image && (
          <div className={clsx(styles.itemImageContainer)}>
            <Image image={image} className={styles.itemImage} />
          </div>
        )}
        {listType === 'withNumber' && (
          <span className={styles.itemNumber}>{index + 1 < 10 ? `0${index + 1}` : index + 1}</span>
        )}
        {listType === 'withBullet' && (
          <div className={styles.itemBulletContainer}>
            <span className={styles.itemBullet}></span>
          </div>
        )}
        <div className={styles.textContainer}>
          <h3 className={styles.itemTitle}>
            {title}
            {additionalExplanation && (
              <FaRegQuestionCircle
                title={additionalExplanation}
                className={styles.questionIcon}
              ></FaRegQuestionCircle>
            )}
          </h3>

          {typeof text === 'string' ? (
            <p className={styles.itemText}>{text}</p>
          ) : (
            <BlockContent
              blocks={text}
              renderContainerOnSingleChild
              serializers={serializers}
              className={styles.itemText}
            />
          )}
          {link && (
            <TextLink to={getUrlFromVersatileLink(link)} className={styles.link} withArrowIcon>
              {link.text}
            </TextLink>
          )}
        </div>
      </div>
    </li>
  );
};

export default ListItem;
